import React, { useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
import { debounce } from "lodash";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { useSocialLinks, SocialPlatform } from "@ugg/shared/api/requests/accounts/profile-customizations";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgLolTwitchIcon } from "@ugitgud/legos/ui/icon/SVG/LolTwitchIcon";
import { SvgLolXIcon } from "@ugitgud/legos/ui/icon/SVG/LolXIcon";
import { SvgLolYoutubeIcon } from "@ugitgud/legos/ui/icon/SVG/LolYoutubeIcon";

interface SocialMediaInputProps {
  label: string;
  prefix: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  icon: React.ReactNode;
  iconBgColor: string;
  placeholder: string;
  isLast?: boolean;
}

const SocialMediaInput: React.FC<SocialMediaInputProps> = ({
  label,
  prefix,
  value,
  onChange,
  onBlur,
  icon,
  iconBgColor,
  placeholder,
  isLast = false,
}) => {
  return (
    <div className={classNames({ "border-b border-purple-100 mb-4.5 pb-4.5": !isLast })}>
      <div className="text-lavender-50 text-xs mb-1.5">{label}</div>
      <div className="flex items-center bg-purple-100 rounded-l-[3px] h-9 max-w-[300px]">
        <div
          className="flex items-center justify-center w-9 h-9 rounded-l-[3px] shrink-0"
          style={{ backgroundColor: iconBgColor }}
        >
          <div className="w-5 h-5 text-white">{icon}</div>
        </div>
        <div className="flex items-center flex-1 p-3">
          <span className="text-lavender-200 text-xs whitespace-nowrap select-none pointer-events-none">{prefix}</span>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            className="bg-transparent border-none outline-none text-white text-xs w-full placeholder-lavender-200"
          />
        </div>
      </div>
    </div>
  );
};

const SocialMedia: React.FC = () => {
  const [twitchUsername, setTwitchUsername] = useState("");
  const [youtubeUsername, setYoutubeUsername] = useState("");
  const [xUsername, setXUsername] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");

  const { updateSocialLink, deleteSocialLink, saving, loading, currentValues, hasRecurlySubscription } = useSocialLinks({
    onUpdateCompleted: () => {
      setSaveSuccess(true);
      setSaveError("");
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onUpdateError: (error) => {
      setSaveError(error.message || "Failed to save changes");
      setSaveSuccess(false);
    },
    onDeleteCompleted: () => {
      setSaveSuccess(true);
      setSaveError("");
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onDeleteError: (error) => {
      setSaveError(error.message || "Failed to delete");
      setSaveSuccess(false);
    },
  });

  // Initialize form with existing data
  useEffect(() => {
    if (currentValues.twitchName) setTwitchUsername(currentValues.twitchName);
    if (currentValues.youtubeName) setYoutubeUsername(currentValues.youtubeName);
    if (currentValues.twitterName) setXUsername(currentValues.twitterName);
  }, [currentValues.twitchName, currentValues.youtubeName, currentValues.twitterName]);

  // Debounced save function for each platform
  const debouncedSaveTwitch = useRef(
    debounce((username: string) => {
      if (username) {
        updateSocialLink("twitch", username);
      } else {
        deleteSocialLink("twitch");
      }
    }, 500),
  ).current;

  const debouncedSaveYoutube = useRef(
    debounce((username: string) => {
      if (username) {
        updateSocialLink("youtube", username);
      } else {
        deleteSocialLink("youtube");
      }
    }, 500),
  ).current;

  const debouncedSaveX = useRef(
    debounce((username: string) => {
      if (username) {
        updateSocialLink("x", username);
      } else {
        deleteSocialLink("x");
      }
    }, 500),
  ).current;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSaveTwitch.cancel();
      debouncedSaveYoutube.cancel();
      debouncedSaveX.cancel();
    };
  }, [debouncedSaveTwitch, debouncedSaveYoutube, debouncedSaveX]);

  // Check if values are dirty (different from server values)
  const isDirty = useCallback(
    (field: "twitch" | "youtube" | "x", value: string) => {
      switch (field) {
        case "twitch":
          return value !== (currentValues.twitchName || "");
        case "youtube":
          return value !== (currentValues.youtubeName || "");
        case "x":
          return value !== (currentValues.twitterName || "");
        default:
          return false;
      }
    },
    [currentValues],
  );

  const handleBlur = useCallback(
    (field: "twitch" | "youtube" | "x") => {
      const currentFieldValue = field === "twitch" ? twitchUsername : field === "youtube" ? youtubeUsername : xUsername;

      if (!isDirty(field, currentFieldValue)) {
        return; // No changes, skip save
      }

      switch (field) {
        case "twitch":
          debouncedSaveTwitch(currentFieldValue);
          break;
        case "youtube":
          debouncedSaveYoutube(currentFieldValue);
          break;
        case "x":
          debouncedSaveX(currentFieldValue);
          break;
      }
    },
    [twitchUsername, youtubeUsername, xUsername, isDirty, debouncedSaveTwitch, debouncedSaveYoutube, debouncedSaveX],
  );

  // Don't render if not premium or still loading
  if (loading) {
    return null;
  }

  if (!hasRecurlySubscription) {
    return null;
  }

  return (
    <div className="settings-section social-media">
      <div className="section-content">
        <HeaderBullet className="mb-4.5 flex items-center border-b border-purple-100 pb-4.5">
          <div>Profile Social Media</div>
          <p className="text-lavender-50 text-sm ml-3 font-normal">
            Note: These will be publicly displayed on an active <span className="text-accent-yellow-400">PLUS</span> profile.
            &nbsp;
            <a href="/plus?utm_source=uggsettings" className="text-accent-blue-400 underline">
              Learn More.
            </a>
          </p>
        </HeaderBullet>

        <SocialMediaInput
          label="Twitch"
          prefix="twitch.tv/"
          value={twitchUsername}
          onChange={setTwitchUsername}
          onBlur={() => handleBlur("twitch")}
          icon={<Icon icon={<SvgLolTwitchIcon />} className="w-[20px] h-[20px]" />}
          iconBgColor="#9146FF"
          placeholder="Enter your Twitch username"
        />

        <SocialMediaInput
          label="YouTube"
          prefix=""
          value={youtubeUsername}
          onChange={setYoutubeUsername}
          onBlur={() => handleBlur("youtube")}
          icon={<Icon icon={<SvgLolYoutubeIcon />} className="w-[20px] h-[20px] text-white" />}
          iconBgColor="#FF0000"
          placeholder="Enter your YouTube username"
        />

        <SocialMediaInput
          label="X"
          prefix="x.com/"
          value={xUsername}
          onChange={setXUsername}
          onBlur={() => handleBlur("x")}
          icon={<Icon icon={<SvgLolXIcon />} className="w-[20px] h-[20px]" />}
          iconBgColor="#000000"
          placeholder="Enter your X username"
          isLast
        />

        {(saveSuccess || saveError || saving) && (
          <div className="field-group mt-4">
            {saving && <div className="text-lavender-200 text-xs">Saving...</div>}
            {saveSuccess && <div className="text-green-500 text-xs font-bold">Changes saved successfully!</div>}
            {saveError && <div className="text-red-500 text-xs font-bold">{saveError}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialMedia;
